/* verqo — PreviewNavHeader (shared, marketing site)
   Sticky floating header wrapping the mega-menu.
   Auto-detects persona: /buyers/* -> buyer, else seller.
*/
import { useLocation, Link } from "react-router-dom";
import { VerqoLockup } from "../VerqoLockup";
import { MegaMenu } from "./MegaMenu";
import { SELLER_NAV } from "../../config/nav.seller";
import { BUYER_NAV } from "../../config/nav.buyer";
import { ArrowRight, ArrowLeft } from "lucide-react";

export function PreviewNavHeader({ modeOverride }) {
  const { pathname } = useLocation();
  const isBuyer = modeOverride
    ? modeOverride === "buyer"
    : pathname.startsWith("/buyers");

  const items = isBuyer ? BUYER_NAV : SELLER_NAV;
  const flipHref = isBuyer ? "/" : "/buyers";
  const flipLabel = isBuyer ? "For sellers" : "For buyers";
  const FlipIcon = isBuyer ? ArrowLeft : ArrowRight;
  const cta = isBuyer
    ? { label: "Request invite", href: "/waitlist?as=buyer" }
    : { label: "Get started",   href: "/waitlist?as=seller" };
  const login = isBuyer ? "/login?as=buyer" : "/login?as=seller";
  const home  = isBuyer ? "/buyers" : "/";

  return (
    <header
      className="sticky top-0 inset-x-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200/60"
      data-testid={`preview-nav-header-${isBuyer ? "buyer" : "seller"}`}
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 h-[84px] flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Link
            to={home}
            className="group flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 rounded-md"
            data-testid="preview-nav-brand"
          >
            <VerqoLockup size="xl" />
            <span
              className="ml-1 hidden lg:inline text-[12px] text-slate-500 group-hover:text-slate-900 transition-colors whitespace-nowrap"
              data-testid="preview-nav-tagline"
            >
              · Wholesale, instant.
            </span>
            {isBuyer && (
              <span className="text-[10.5px] uppercase tracking-[0.22em] text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded-full">
                Buyers
              </span>
            )}
          </Link>
          <MegaMenu items={items} />
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={flipHref}
            data-testid="preview-nav-persona-flip"
            className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 transition-colors px-3.5 py-2.5 rounded-md hover:bg-slate-100/70 font-medium"
          >
            {isBuyer && <FlipIcon className="w-4 h-4" />}
            {flipLabel}
            {!isBuyer && <FlipIcon className="w-4 h-4" />}
          </Link>
          <a
            href={login}
            data-testid="preview-nav-signin"
            className="hidden md:inline-flex text-[14px] text-slate-600 hover:text-slate-900 transition-colors px-3.5 py-2.5 rounded-md hover:bg-slate-100/70 font-medium"
          >
            Log in
          </a>
          <a
            href={cta.href}
            data-testid="preview-nav-cta"
            className="inline-flex items-center gap-1.5 text-[14px] px-5 py-2.5 rounded-md text-white hover:opacity-90 transition-opacity font-semibold bg-[#0c1612]"
          >
            {cta.label}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </header>
  );
}
